<script>
  import { onMount, onDestroy } from 'svelte';
  import tippy from 'tippy.js';

  import Avatar from '@components/Avatar.svelte';
  import UserAjax from '@ajax/user';

  import ImageUpload from '@utils/image-upload';
  import Notification from '@utils/notification';
  import CircleCropper from '@utils/circle-cropper';

  import UserFollowButton from '@app-components/UserFollowButton.svelte';

  import { browser } from '$app/environment';
  import Doka from '@utils/doka';
  import { getCurrentUserStore } from '@stores/current_user';
  import queryString from 'query-string';
  import ProfileOptionMenu from '@app-components/profile/ProfileOptionMenu.svelte';

  export let user;
  export let mobile = false;
  export let imageSize = 'medium';
  export let userBlocked = false;

  let currentUser = getCurrentUserStore('default');
  let sub = currentUser.subscribe((x) => (currentUser = x));

  let fileInput;
  let avatarCropper;
  let updateProfileImage = async function (url) {
    user.avatar_url = url;
    let resp = await UserAjax.update(user, { avatar_url: url });
    currentUser.set({ ...currentUser, ...resp });
  };

  let attachImageUpload = function (node) {
    if (!user.permissions.edit || !browser) {
      return;
    }

    fileInput = node;

    let cropper = Doka().create({
      utils: ['crop'],
      cropAspectRatio: 0.22,
      allowButtonCancel: false,
    });

    ImageUpload.attach(
      fileInput,
      async (data) => {
        if (data && data.length > 0) {
          let response = data[0];

          await UserAjax.update(currentUser, {
            cover_photo_url: response.response.uploadURL,
          });

          user = currentUser;

          currentUser.set({
            ...currentUser,
            cover_photo_url: response.response.uploadURL,
          });

          Notification.send({
            text: '<i class="fa fa-check" style="color: #90ee90"></i><span style="margin-left: 12px;">Profile updated</span>',
            layout: mobile ? 'topCenter' : 'bottomLeft',
          });
        }
      },
      () => {},
      {
        cropper: cropper,
        maxNumberOfFiles: 1,
      }
    );
  };

  let setupTippy = function () {
    tippy('[data-tippy-content]');
  };

  let autoFollow = false;

  let setAutoFollow = function () {
    if (browser && currentUser && currentUser.id && user && user.id && currentUser.id != user.id) {
      autoFollow = queryString.parse(location.search).auto_follow == '1';
    }
  };

  onMount(async () => {
    avatarCropper = await CircleCropper();
    setupTippy();
    setAutoFollow();
  });

  onDestroy(() => {
    sub();
  });
</script>

<style style="text/scss" lang="scss">
  .profile-header-container {
    margin-bottom: 15px;
  }

  .profile-image {
    width: 100%;
    height: 220px;
    border-radius: 8px;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    background-size: cover;
    background-position: center;

    &.placeholder {
      height: 120px;
      background-color: #ffffff;
    }
    &.edit {
      cursor: pointer;
    }
  }

  .profile-actions {
    margin-right: 8px;
    margin-bottom: 8px;
    display: flex;
    flex-direction: row;
    align-items: center; // Add this line to vertically align items
  }

  .avatar-container {
    position: relative;
    top: -75px;
    left: calc(50% - 83px);
    margin-bottom: -75px;
    clear: both;
    width: 166px;
    border: 8px solid #fff;
    border-radius: 100%;
    background: #fff;
    cursor: pointer;

    &.small {
      left: 21px;
      width: 96px;
    }

    &.spotlight.medium {
      background: none;
      border-width: 4px;
      width: 158px;
    }

    :global(.avatar.not-clickable) {
      cursor: default;
    }
  }
  .profile-actions {
    position: relative;
    top: -93px;
    margin-bottom: -75px;
    clear: both;
  }

  .profile-follow-button {
    width: 120px;
  }

  .action-menu {
    margin-left: 20px;
  }

  @media only screen and (max-width: 1170px) {
    .profile-image {
      height: 120px;
    }
  }

  .mobile {
    &.profile-header-container {
      margin-bottom: 38px;
    }
    // &.placeholder {
    //   margin-bottom: 54px;
    // }

    .avatar-container {
      top: -44px;
      border: 4px solid #ffffff;
    }

    // &.placeholder {
    //   .avatar-container {
    //     top: -30px;
    //   }
    // }

    .profile-image {
      border-radius: 0px;
      align-items: flex-start;
      &.placeholder {
        height: 80px;
      }
      .profile-actions {
        margin-bottom: unset;
        margin-top: 8px;
      }
    }

    .profile-actions {
      clear: both;
      right: 10px;
      top: -53px;
      // &.has-placeholder {
      //   top: -77px;
      // }
    }
  }
</style>

<div class="profile-header-container" class:mobile={mobile} class:placeholder={!user.cover_photo_url}>
  <div
    class="profile-image"
    class:placeholder={!user.cover_photo_url}
    class:edit={user.permissions.edit}
    style={user.cover_photo_url
      ? `background-image:url(${user.cover_photo_url})`
      : `background-image:url(/images/cover-placeholder.png);`}
    use:attachImageUpload />

  <div class="avatar-container {imageSize}" class:spotlight={user.spotlight_active}>
    {#key avatarCropper}
      <Avatar
        src={user.avatar_url}
        user={user}
        mobile={mobile}
        width={imageSize == 'small' ? 88 : 150}
        allowImageUpload={!user.spotlight_active && user.permissions.edit}
        plusIcon={user.avatar_url.includes('ui-avatars.com')}
        cropper={avatarCropper}
        class={user.permissions.edit ? '' : 'not-clickable'}
        on:upload-complete={(e) => {
          if (e && e.detail && e.detail.images && e.detail.images.length > 0 && e.detail.images[0].response) {
            updateProfileImage(e.detail.images[0].response.uploadURL);
            Notification.send({
              text: '<i class="fa fa-check" style="color: #90ee90"></i><span style="margin-left: 12px;">Profile updated</span>',
              layout: mobile ? 'topCenter' : 'bottomLeft',
            });
          }
        }} />
    {/key}
  </div>
</div>

<div class:mobile={mobile}>
  {#if !userBlocked}
    <div class="profile-actions" class:mobile={mobile} style="float: right">
      <div class="profile-follow-button">
        {#if browser}
          {#if autoFollow}
            <UserFollowButton mobile={mobile} user={user} size={imageSize} context="other_profile" autoFollow={true} />
          {:else}
            <UserFollowButton mobile={mobile} user={user} size={imageSize} context="other_profile" />
          {/if}
        {/if}
      </div>
      {#if user.id != currentUser.id}
        <div class="action-menu" style="width: 30px">
          <ProfileOptionMenu profileUser={user} />
        </div>
      {/if}
    </div>
  {/if}
</div>
